import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { FaTrophy } from 'react-icons/fa'
import { fetchPublicLeaderboard } from '../utils/api'

const LeaderboardSection = () => {
  const ranks = [
    { title: 'Heritage Hero', icon: '🏆', color: 'from-accent-maroon to-accent-maroonDark', members: 345 },
    { title: 'Lake Protector', icon: '💧', color: 'from-accent-maroon to-accent-maroonDark', members: 567 },
    { title: 'City Guardian', icon: '🛡️', color: 'from-accent-maroon to-accent-maroonDark', members: 234 },
    { title: 'Samriddhi Champion', icon: '⭐', color: 'from-accent-maroon to-accent-maroonDark', members: 89 },
    { title: 'Elite Volunteer', icon: '👑', color: 'from-accent-maroon to-accent-maroonDark', members: 12 },
  ]

  const [topVolunteers, setTopVolunteers] = useState([
    { id: 1, name: 'Rajesh Kumar', hours: 450, missions: 24, avatar: '👨‍💼' },
    { id: 2, name: 'Priya Sharma', hours: 420, missions: 22, avatar: '👩‍💼' },
    { id: 3, name: 'Vikram Singh', hours: 380, missions: 19, avatar: '👨‍🦱' },
    { id: 4, name: 'Sneha Joshi', hours: 365, missions: 18, avatar: '👩‍🦰' },
    { id: 5, name: 'Arun Patel', hours: 340, missions: 17, avatar: '👨‍🎓' },
  ])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const getLeaderboardData = async () => {
      try {
        const res = await fetchPublicLeaderboard()
        if (res.data?.data && Array.isArray(res.data.data) && res.data.data.length > 0) {
          const sorted = [...res.data.data].sort((a, b) => b.hours - a.hours)
          setTopVolunteers(sorted)
        }
      } catch (error) {
        console.error('Error fetching leaderboard data:', error)
      } finally {
        setLoading(false)
      }
    }
    getLeaderboardData()
  }, [])

  return (
    <section id="leaderboard" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: false }} className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold font-grotesk mb-4">
            <span className="bg-gradient-to-r from-accent-maroon to-accent-maroonDark bg-clip-text text-transparent">
              Leaderboard & Gamification
            </span>
          </h2>
          <p className="text-gray-600 text-lg">Celebrate our top contributors and achievements</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <h3 className="text-2xl font-bold text-gray-800 mb-6">Achievement Ranks</h3>
            <div className="space-y-4">
              {ranks.map((rank, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: false }}
                  transition={{ delay: index * 0.1 }}
                  className="glass-card flex items-center justify-between group cursor-pointer bg-white/10 border border-gray-200"
                >
                  <div className="flex items-center space-x-4">
                    <div className="text-4xl">{rank.icon}</div>
                    <div>
                      <h4 className="font-bold text-gray-800">{rank.title}</h4>
                      <p className="text-sm text-gray-600">{rank.members} members</p>
                    </div>
                  </div>
                  <FaTrophy className={`w-5 h-5 text-accent-maroon group-hover:scale-110 transition`} />
                </motion.div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-2xl font-bold text-gray-800 mb-6">Top Volunteers</h3>
            <div className="space-y-3">
              {topVolunteers.map((volunteer, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: false }}
                  transition={{ delay: index * 0.1 }}
                  className="glass-card p-4 flex items-center justify-between group hover:border-accent-maroon/50 transition bg-white/10 border border-gray-200"
                >
                  <div className="flex items-center space-x-4 flex-1">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${
                      index === 0
                        ? 'bg-gradient-to-br from-accent-maroon to-accent-maroonDark'
                        : index === 1
                        ? 'bg-gradient-to-br from-gray-300 to-gray-500'
                        : index === 2
                        ? 'bg-gradient-to-br from-accent-maroon to-accent-maroonDark'
                        : 'bg-gradient-to-br from-gray-600 to-gray-800'
                    }`}>
                      {index + 1}
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="text-lg">{volunteer.avatar}</span>
                        <h4 className="font-bold text-gray-800">{volunteer.name}</h4>
                      </div>
                      <div className="flex space-x-4 text-xs text-gray-600">
                        <span>{volunteer.hours} hours</span>
                        <span>•</span>
                        <span>{volunteer.missions} missions</span>
                      </div>
                    </div>
                  </div>

                  <motion.div whileHover={{ scale: 1.1 }} className="px-3 py-1 bg-gradient-to-r from-accent-maroon/20 to-accent-maroonDark/20 border border-accent-maroon/50 rounded-full text-xs font-semibold text-accent-maroon">
                    {volunteer.hours * 10} XP
                  </motion.div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default LeaderboardSection